import { Store } from "../core/state";
import { createMediaQuery, type MediaQuerySubscription } from "../core/media-query";
import { el, applyStyles, clearElement } from "../core/dom";
import { fetchConsentContentPublic, submitConsentEventPublic } from "../api";
import { resolveDisplayLanguage } from "../api/language";
import {
  fetchNoticeConsentStatusForScope,
  isNoticeFullyConsented,
} from "../api/notice-consent-status";
import { resolveEndpointOptions, type ResolvedEndpoints } from "../shared/endpoints";
import { injectCheckboxStyles } from "../styles/inject";
import { inlineStyles } from "../styles/inline";
import { renderInlinePurposes, type PurposeRenderRefs } from "./purposes";
import { buildInlineLook, type InlineLook } from "./look";
import { DARK_SCHEME_QUERY } from "../appearance/constants";
import { NoticeCustomCss } from "../appearance/custom-css";
import { injectSwitchStyles } from "../appearance/stylesheets";
import { applyCssVariables } from "../appearance/theme-vars";
import { openConfirmDialog } from "../modal/confirm-dialog";
import { injectNoticeFontStyles, resolveNoticeFontFamily } from "../modal/notice-font";
import {
  buildPreselection,
  preselectionForRows,
  resolvePurposePreselection,
} from "../modal/preselection";
import { NOTICE_FONT_ROOT_ATTRIBUTE } from "../constants";
import { productPurposeKey } from "../core/productMatrix";
import {
  allRequiredElementsChecked,
  buildInlineSubmitPurposes,
  initialCollapsedPurposes,
  initialSelection,
  inlinePurposeRows,
  inlineSubmissionKey,
  isRetryableFailure,
  toggleDataElement,
  withPurposeElements,
} from "./selection";
import type { ConfirmDialogRefs, InlineOptions } from "../types";
import type { ActiveConfig, TranslationObject } from "../api/types";

type InlineState = {
  content: ActiveConfig | null;
  isLoading: boolean;
  isSubmitting: boolean;
  selectedLanguage: string;
  collapsedPurposes: Record<string, boolean>;
  selectedPurposes: Record<string, boolean>;
  selectedDataElements: Record<string, boolean>;
  hasAlreadyConsented: boolean;
  // Blocks auto-submit while the Go ledger check-consent read is in flight,
  // so an already-consented principal is not re-submitted post-cutover.
  isCheckingConsent: boolean;
  fetchError: Error | null;
  errorMessage: string | null;
};

/** The selection a confirm answer was given for. */
const selectionSignature = (state: {
  selectedPurposes: Record<string, boolean>;
  selectedDataElements: Record<string, boolean>;
}): string => JSON.stringify([state.selectedPurposes, state.selectedDataElements]);

export class InlineController {
  private store: Store<InlineState>;
  private options: InlineOptions;
  private readonly endpoints: ResolvedEndpoints;
  private targetEl: HTMLElement | null = null;
  private hostEl: HTMLElement | null = null;
  private shadowRoot: ShadowRoot | null = null;
  private rootEl: HTMLElement | null = null;
  private mediaQuery: MediaQuerySubscription;
  private purposeRefs: PurposeRenderRefs | null = null;
  private errorEl: HTMLElement | null = null;
  private hasSubmitted = false;
  private lastSubmissionKey: string | null = null;
  private hasCheckedLedgerConsent = false;
  private fetchedWithToken = false;
  private fetchGeneration = 0;
  private unsubscribe: (() => void) | null = null;
  private abortController: AbortController | null = null;
  private colorScheme: MediaQuerySubscription;
  private customCss = new NoticeCustomCss();
  private look: InlineLook | null = null;
  // The embed has no accept button -- its only submit is the automatic one --
  // so the confirm step gates that. Keyed on the selection it was asked
  // about: declining stops this submission without silencing the embed for a
  // selection the visitor goes on to change, and a confirmed one is not asked
  // about again once it has been sent.
  private confirmDialog: ConfirmDialogRefs | null = null;
  private refusedSelection: string | null = null;
  private confirmedSelection: string | null = null;

  constructor(options: InlineOptions) {
    this.options = options;
    this.endpoints = resolveEndpointOptions(options.baseUrl, options.ledgerBaseUrl);
    this.store = new Store<InlineState>({
      content: null,
      isLoading: true,
      isSubmitting: false,
      selectedLanguage: options.language || "en",
      collapsedPurposes: {},
      selectedPurposes: {},
      selectedDataElements: {},
      hasAlreadyConsented: false,
      isCheckingConsent: false,
      fetchError: null,
      errorMessage: null,
    });
    this.mediaQuery = createMediaQuery("(max-width: 768px)");
    // Automatic dark mode follows the device's scheme while the embed is up.
    this.colorScheme = createMediaQuery(DARK_SCHEME_QUERY);
  }

  async init(): Promise<void> {
    this.abortController = new AbortController();

    // Resolve target element
    if (typeof this.options.target === "string") {
      this.targetEl = document.querySelector(this.options.target);
      if (!this.targetEl) {
        throw new Error(`Target element not found: ${this.options.target}`);
      }
    } else {
      this.targetEl = this.options.target;
    }

    // Mount the inline form inside an open shadow root attached to a host
    // <div> within the user's target container. This matches the modal's
    // isolation strategy so host-page CSS cannot bleed into the inline form
    // either.
    this.hostEl = document.createElement("div");
    this.hostEl.setAttribute("data-redacto-consent-inline", "");
    // Block-level, but no `all: initial` — we want font / color / line-height
    // to inherit from the user's page so the inline form blends in like the
    // React SDK does.
    this.hostEl.style.display = "block";
    this.targetEl.appendChild(this.hostEl);
    this.shadowRoot = this.hostEl.attachShadow({ mode: "open" });
    injectCheckboxStyles(this.shadowRoot);
    injectNoticeFontStyles(this.shadowRoot);

    // Subscribe to state changes
    this.unsubscribe = this.store.subscribe((state, prevState) => {
      this.handleStateChange(state, prevState);
    });

    // Subscribe to media query changes
    this.mediaQuery.subscribe(() => this.rebuildUI());
    this.colorScheme.subscribe(() => this.rebuildUI());

    // Render loading state
    this.renderLoading();

    // Fetch content
    await this.fetchNotice();
  }

  destroy(): void {
    // Cancels the in-flight ledger read and marks the controller torn down, so
    // late-resolving work cannot fire host callbacks or submit consent.
    this.abortController?.abort();
    this.unsubscribe?.();
    this.mediaQuery.destroy();
    this.colorScheme.destroy();
    this.confirmDialog?.dispose();
    this.confirmDialog = null;
    this.customCss.remove();
    this.store.destroy();
    // Removing the host div tears down the shadow tree (and the rootEl
    // inside it) in one step.
    if (this.hostEl && this.hostEl.parentNode) {
      this.hostEl.parentNode.removeChild(this.hostEl);
    }
    this.hostEl = null;
    this.shadowRoot = null;
    this.rootEl = null;
    this.purposeRefs = null;
  }

  updateToken(accessToken: string): void {
    this.options = { ...this.options, accessToken };
    // Both guards are released together: a refusal held past a new token
    // would freeze the embed for that selection with no way back to a dialog.
    this.refusedSelection = null;
    this.confirmedSelection = null;
    if (accessToken && !this.fetchedWithToken) {
      void this.fetchNotice();
      return;
    }
    // A late-arriving token is the first chance to run the ledger
    // check-consent read; it gates auto-submit synchronously before trying.
    if (this.shouldCheckLedgerConsent()) {
      void this.checkLedgerConsent();
      return;
    }
    this.tryAutoSubmit();
  }

  // ─── Private ───

  // destroy() aborts the controller, so an aborted signal is the marker that
  // teardown has happened and async continuations must not touch the host.
  private isDestroyed(): boolean {
    return Boolean(this.abortController?.signal.aborted);
  }

  private async fetchNotice(): Promise<void> {
    const generation = ++this.fetchGeneration;
    this.fetchedWithToken = Boolean(this.options.accessToken);
    this.store.setState({ isLoading: true, fetchError: null, hasAlreadyConsented: false });
    // With the API host, a token-authenticated read goes to the ledger like the
    // modal's; the tokenless and magic-link reads stay on the consent server.
    const noticeBaseUrl =
      this.endpoints.mode === "host" && this.options.accessToken && !this.options.applicationId
        ? this.endpoints.ledgerBaseUrl
        : this.endpoints.baseUrl;
    try {
      const data = await fetchConsentContentPublic({
        org_uuid: this.options.org_uuid,
        workspace_uuid: this.options.workspace_uuid,
        notice_uuid: this.options.notice_uuid,
        accessToken: this.options.accessToken,
        baseUrl: noticeBaseUrl,
        language: this.options.language,
        specific_uuid: this.options.applicationId,
      });

      if (this.isDestroyed() || generation !== this.fetchGeneration) {
        return;
      }

      const activeConfig = data.detail.active_config;
      const rows = inlinePurposeRows(activeConfig);
      // Seeded from the notice's pre-selection mode, on the keys the rows read
      // (per product and purpose once the notice groups): NONE leaves
      // everything unticked, as it always did.
      const selection = {
        ...initialSelection(rows),
        ...preselectionForRows(
          rows,
          buildPreselection(
            activeConfig.purposes,
            resolvePurposePreselection(activeConfig.purpose_preselection)
          )
        ),
      };

      // Post-cutover, consent records live in the Go ledger, so Python's
      // get-notice no longer 409s for an already-consented principal. Kick off
      // the ledger check BEFORE the content state lands: its synchronous
      // prologue raises isCheckingConsent, so the content state change below
      // cannot auto-submit until the read settles.
      void this.checkLedgerConsent();

      this.store.setState({
        content: activeConfig,
        isLoading: false,
        selectedLanguage: resolveDisplayLanguage(
          this.options.language,
          activeConfig.default_language,
          activeConfig.supported_languages_and_translations
        ),
        collapsedPurposes: initialCollapsedPurposes(rows),
        selectedPurposes: selection.selectedPurposes,
        selectedDataElements: selection.selectedDataElements,
      });
    } catch (err: unknown) {
      if (this.isDestroyed() || generation !== this.fetchGeneration) {
        return;
      }
      const error = err as Error & { status?: number };
      if (error.status === 409) {
        this.store.setState({ hasAlreadyConsented: true, isLoading: false });
        this.options.onAccept?.();
      } else {
        this.store.setState({ fetchError: error, isLoading: false });
        this.options.onError?.(error);
      }
    }
  }

  private shouldCheckLedgerConsent(): boolean {
    return (
      Boolean(this.endpoints.ledgerBaseUrl) &&
      !this.options.applicationId &&
      Boolean(this.options.accessToken) &&
      !this.hasCheckedLedgerConsent
    );
  }

  // Ask the Go ledger whether this principal has already fully consented —
  // the ledger equivalent of the legacy Python 409. Skipped without a ledger
  // (the 409 path governs) and for the magic-link applicationId flow. Any
  // failure returns null and falls through to the normal flow.
  private async checkLedgerConsent(): Promise<void> {
    if (!this.shouldCheckLedgerConsent()) return;
    this.hasCheckedLedgerConsent = true;
    this.store.setState({ isCheckingConsent: true });
    try {
      const status = await fetchNoticeConsentStatusForScope({
        accessToken: this.options.accessToken!,
        ledgerBaseUrl: this.endpoints.ledgerBaseUrl,
        organisationUuid: this.options.org_uuid,
        workspaceUuid: this.options.workspace_uuid,
        noticeUuid: this.options.notice_uuid,
        signal: this.abortController?.signal,
      });
      if (this.isDestroyed()) {
        return;
      }
      if (status && isNoticeFullyConsented(status)) {
        this.store.setState({ hasAlreadyConsented: true });
        this.options.onAccept?.();
      }
    } finally {
      if (!this.isDestroyed()) {
        this.store.setState({ isCheckingConsent: false });
        // Re-run auto-submit now that the gate is down (no-op when already
        // consented or requirements are unmet).
        this.tryAutoSubmit();
      }
    }
  }

  private handleStateChange(state: InlineState, prevState: InlineState): void {
    // Loading → content loaded
    if (prevState.isLoading && !state.isLoading && state.content) {
      this.rebuildUI();
    }

    // Already consented or fetch error → hide
    if (state.hasAlreadyConsented || state.fetchError) {
      if (this.rootEl && this.shadowRoot && this.rootEl.parentNode === this.shadowRoot) {
        this.shadowRoot.removeChild(this.rootEl);
        this.rootEl = null;
      }
      return;
    }

    // Checkbox state changes → update checkboxes
    if (state.selectedPurposes !== prevState.selectedPurposes ||
        state.selectedDataElements !== prevState.selectedDataElements) {
      this.updateCheckboxes(state);
      this.notifyValidation(state);
      this.tryAutoSubmit();
    }

    // Collapse state changes
    if (state.collapsedPurposes !== prevState.collapsedPurposes) {
      this.updateCollapseState(state);
    }

    // Error message changes
    if (state.errorMessage !== prevState.errorMessage) {
      this.updateErrorMessage(state);
    }
  }

  private renderLoading(): void {
    if (!this.shadowRoot) return;

    const textColor = this.options.settings?.textColor || "#344054";

    this.rootEl = el(
      "div",
      {
        [NOTICE_FONT_ROOT_ATTRIBUTE]: "",
        style: {
          ...inlineStyles.loadingContainer,
          fontFamily: resolveNoticeFontFamily({ hostFont: this.options.settings?.font }),
        },
      },
      el("div", { style: inlineStyles.loadingSpinner }),
      el("p", { style: { fontSize: "16px", fontWeight: 400, color: textColor } }, "Loading...")
    );

    this.shadowRoot.appendChild(this.rootEl);
  }

  private rebuildUI(): void {
    const state = this.store.getState();
    if (!state.content || !this.shadowRoot) return;
    // The embed stays hidden once consent is on record or the fetch failed.
    if (state.hasAlreadyConsented || state.fetchError) return;

    // Remove existing root
    if (this.rootEl && this.rootEl.parentNode === this.shadowRoot) {
      this.shadowRoot.removeChild(this.rootEl);
    }

    // Build error message element
    this.errorEl = el("div", {
      role: "alert",
      style: {
        backgroundColor: "#FEE2E2",
        color: "#DC2626",
        padding: "12px",
        borderRadius: "6px",
        fontSize: "14px",
        border: "1px solid #FCA5A5",
        display: state.errorMessage ? "block" : "none",
      },
    });
    if (state.errorMessage) {
      this.errorEl.textContent = state.errorMessage;
    }

    const look = buildInlineLook({
      noticeId: this.options.notice_uuid,
      content: state.content,
      settings: this.options.settings,
      prefersDark: this.colorScheme.matches,
      isMobile: this.mediaQuery.matches,
      fontFamily: resolveNoticeFontFamily({
        hostFont: this.options.settings?.font,
        noticeFont: state.content.font_preference,
      }),
    });
    this.look = look;
    if (look.drawsSwitches) injectSwitchStyles(this.shadowRoot);
    this.customCss.apply(this.shadowRoot, this.options.notice_uuid, look.appearance.customCss);

    // Build purposes
    this.purposeRefs = renderInlinePurposes(state.content.purposes, {
      selectedPurposes: state.selectedPurposes,
      collapsedPurposes: state.collapsedPurposes,
      selectedDataElements: state.selectedDataElements,
      products: state.content.products,
      productPurposeOrder: state.content.product_purpose_order,
      settings: this.options.settings,
      look,
      language: state.selectedLanguage,
      isMobile: this.mediaQuery.matches,
      getTranslatedText: (key, defaultText, itemId) =>
        this.getTranslatedText(key, defaultText, itemId),
      onPurposeToggle: (uuid, productUuid) => this.handlePurposeCheckboxChange(uuid, productUuid),
      onPurposeCollapse: (uuid, productUuid) => this.togglePurposeCollapse(uuid, productUuid),
      onDataElementToggle: (elementUuid, purposeUuid, productUuid) =>
        this.handleDataElementCheckboxChange(elementUuid, purposeUuid, productUuid),
    });

    this.rootEl = el(
      "div",
      { ...look.rootAttributes, style: look.rootStyle },
      this.errorEl,
      this.purposeRefs.container
    );
    applyCssVariables(this.rootEl, look.rootVariables);

    this.shadowRoot.appendChild(this.rootEl);
    this.notifyValidation(state);
  }

  private getTranslatedText(key: string, defaultText: string, itemId?: string): string {
    const state = this.store.getState();
    const content = state.content;
    if (!content) return defaultText;

    const lang = state.selectedLanguage;
    if (lang === "English" || lang === "en" || lang === "EN") {
      if (key === "privacy_policy_anchor_text" && content.privacy_policy_anchor_text) {
        return content.privacy_policy_anchor_text;
      }
      return defaultText;
    }

    const translations = content.supported_languages_and_translations;
    if (!translations) return defaultText;

    const translationMap = translations[lang] as TranslationObject | undefined;
    if (!translationMap) return defaultText;

    if (itemId) {
      if (key === "purposes.name" || key === "purposes.description") {
        const purposeData = translationMap.purposes?.[itemId];
        if (purposeData) {
          if (typeof purposeData === "string") {
            return key === "purposes.name" ? purposeData : defaultText;
          }
          return key === "purposes.name"
            ? purposeData.name || defaultText
            : purposeData.description || defaultText;
        }
        return defaultText;
      }
      if (key === "products.name" || key === "products.description") {
        const productData = translationMap.products?.[itemId];
        if (productData) {
          if (typeof productData === "string") {
            return key === "products.name" ? productData : defaultText;
          }
          return key === "products.name"
            ? productData.name || defaultText
            : productData.description || defaultText;
        }
        return defaultText;
      }
      if (key.startsWith("data_elements.")) {
        return translationMap.data_elements?.[itemId] || defaultText;
      }
    }

    const value = (translationMap as any)[key];
    if (typeof value === "string" && value) return value;
    return defaultText;
  }

  private togglePurposeCollapse(purposeUuid: string, productUuid?: string): void {
    const state = this.store.getState();
    const key = productPurposeKey(productUuid, purposeUuid);
    this.store.setState({
      collapsedPurposes: {
        ...state.collapsedPurposes,
        [key]: !state.collapsedPurposes[key],
      },
    });
  }

  private handlePurposeCheckboxChange(purposeUuid: string, productUuid?: string): void {
    const state = this.store.getState();
    const content = state.content;
    if (!content) return;

    const purpose = content.purposes.find((p) => p.uuid === purposeUuid);
    if (!purpose) return;

    const key = productPurposeKey(productUuid, purposeUuid);
    const newPurposeState = !state.selectedPurposes[key];

    this.store.setState({
      selectedPurposes: { ...state.selectedPurposes, [key]: newPurposeState },
      selectedDataElements: withPurposeElements(
        purpose,
        productUuid,
        newPurposeState,
        state.selectedDataElements
      ),
    });
  }

  private handleDataElementCheckboxChange(
    elementUuid: string,
    purposeUuid: string,
    productUuid?: string
  ): void {
    const state = this.store.getState();
    const content = state.content;
    if (!content) return;

    const purpose = content.purposes.find((p) => p.uuid === purposeUuid);
    if (!purpose) return;

    const next = toggleDataElement(purpose, elementUuid, productUuid, state.selectedDataElements);

    this.store.setState({
      selectedDataElements: next.selectedDataElements,
      selectedPurposes: {
        ...state.selectedPurposes,
        [productPurposeKey(productUuid, purposeUuid)]: next.purposeSelected,
      },
    });
  }

  private updateCheckboxes(state: InlineState): void {
    if (!this.purposeRefs) return;

    for (const [key, control] of this.purposeRefs.purposeControls) {
      control.setChecked(state.selectedPurposes[key] || false);
    }
    for (const [key, checkbox] of this.purposeRefs.checkboxes) {
      checkbox.checked = state.selectedDataElements[key] || false;
    }
  }

  private updateCollapseState(state: InlineState): void {
    if (!this.purposeRefs) return;

    const rows = state.content ? inlinePurposeRows(state.content) : [];
    const rowsByKey = new Map(
      rows.map((row) => [productPurposeKey(row.productUuid, row.purpose.uuid), row])
    );

    for (const [stateKey, container] of this.purposeRefs.dataElementContainers) {
      const purpose = rowsByKey.get(stateKey)?.purpose;
      const isCollapsed = state.collapsedPurposes[stateKey];

      container.style.display = !isCollapsed && purpose?.data_elements?.length ? "flex" : "none";

      const chevron = this.purposeRefs.chevrons.get(stateKey);
      if (chevron) {
        (chevron as unknown as HTMLElement).style.transform = !isCollapsed
          ? "rotate(90deg)"
          : "rotate(0deg)";
      }

      const collapseBtn = this.purposeRefs.collapseButtons.get(stateKey);
      if (collapseBtn) {
        collapseBtn.setAttribute("aria-expanded", String(!isCollapsed));
      }
    }
  }

  private updateErrorMessage(state: InlineState): void {
    if (!this.errorEl) return;
    if (state.errorMessage) {
      this.errorEl.textContent = state.errorMessage;
      this.errorEl.style.display = "block";
    } else {
      this.errorEl.style.display = "none";
    }
  }

  private areAllRequiredElementsChecked(): boolean {
    const state = this.store.getState();
    const content = state.content;
    if (!content) return false;

    return allRequiredElementsChecked(inlinePurposeRows(content), state.selectedDataElements);
  }

  private notifyValidation(state: InlineState): void {
    const isValid = this.areAllRequiredElementsChecked();
    this.options.onValidationChange?.(isValid);
  }

  private async tryAutoSubmit(skipKey: string | null = null): Promise<void> {
    // A submit is a network write — never start one after teardown.
    if (this.isDestroyed()) {
      return;
    }

    const state = this.store.getState();
    const accessToken = this.options.accessToken;

    if (
      accessToken &&
      state.content &&
      this.areAllRequiredElementsChecked() &&
      !state.isSubmitting &&
      !state.isCheckingConsent &&
      !state.hasAlreadyConsented &&
      !this.hasSubmitted
    ) {
      const submissionKey = inlineSubmissionKey(accessToken, state.content.uuid, state);
      if (submissionKey === this.lastSubmissionKey || submissionKey === skipKey) {
        return;
      }
      if (this.look?.appearance.confirmBeforeSubmit) {
        const signature = selectionSignature(state);
        if (this.refusedSelection === signature || this.confirmedSelection === signature) {
          return;
        }
        this.askToConfirm(signature);
        return;
      }
      await this.runSubmission(accessToken, submissionKey);
    }
  }

  /** Asks "are you sure" before the automatic submission of `signature`. */
  private askToConfirm(signature: string): void {
    if (this.confirmDialog || !this.shadowRoot) return;
    this.confirmDialog = openConfirmDialog({
      root: this.shadowRoot,
      action: "accept-selected",
      language: this.store.getState().selectedLanguage,
      rootAttributes: this.look?.rootAttributes,
      fontFamily: (this.look?.rootStyle.fontFamily as string | undefined) ?? undefined,
      onConfirm: () => {
        this.confirmDialog = null;
        this.confirmedSelection = signature;
        void this.submitConfirmed();
      },
      onCancel: () => {
        this.confirmDialog = null;
        this.refusedSelection = signature;
      },
    });
  }

  private async submitConfirmed(): Promise<void> {
    const state = this.store.getState();
    const accessToken = this.options.accessToken;
    if (this.isDestroyed() || !accessToken || !state.content) return;
    const submitted = await this.runSubmission(
      accessToken,
      inlineSubmissionKey(accessToken, state.content.uuid, state)
    );
    if (!submitted && !this.isDestroyed()) {
      // Released with the failure: holding it would leave the embed with no
      // dialog and no way back to one.
      this.confirmedSelection = null;
      void this.tryAutoSubmit();
    }
  }

  /** Sends one submission; resolves whether it was recorded. */
  private async runSubmission(accessToken: string, submissionKey: string): Promise<boolean> {
    this.hasSubmitted = true;
    this.lastSubmissionKey = submissionKey;
    try {
      await this.handleSubmit(accessToken);
    } catch (err) {
      this.hasSubmitted = false;
      this.options.onError?.(err as Error);
      if (isRetryableFailure(err)) {
        this.lastSubmissionKey = null;
        void this.tryAutoSubmit(submissionKey);
      } else {
        // Picks up a change made while this submit was in flight; the failed
        // selection itself stays skipped under its key.
        void this.tryAutoSubmit();
      }
      return false;
    }
    void this.tryAutoSubmit();
    return true;
  }

  private async handleSubmit(tokenToUse: string): Promise<void> {
    const state = this.store.getState();
    const content = state.content;
    if (!tokenToUse || !content) {
      this.store.setState({ errorMessage: "Unable to submit consent. Please try again." });
      throw new Error("Access token or content not available");
    }

    try {
      this.store.setState({ isSubmitting: true, errorMessage: null });

      await submitConsentEventPublic({
        org_uuid: this.options.org_uuid,
        workspace_uuid: this.options.workspace_uuid,
        accessToken: tokenToUse,
        baseUrl: this.endpoints.baseUrl,
        ledgerBaseUrl: this.endpoints.ledgerBaseUrl,
        noticeUuid: content.notice_uuid,
        purposes: buildInlineSubmitPurposes(inlinePurposeRows(content), state),
        declined: false,
        meta_data: this.options.applicationId
          ? { specific_uuid: this.options.applicationId }
          : undefined,
        language: state.selectedLanguage,
      });

      this.options.onAccept?.();
      this.hasSubmitted = false;
    } catch (error) {
      const err = error as Error;
      const message = err.message || "Failed to submit consent. Please try again.";
      this.store.setState({ errorMessage: message });
      this.hasSubmitted = false;
      throw error;
    } finally {
      this.store.setState({ isSubmitting: false });
    }
  }
}
